import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'
import { pt } from '@/i18n/pt-PT'

type DialogProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  widthClassName?: string
}

// The app's one modal shell — 22 call sites across staff, admin and portal.
//
// RENDERED INTO <body>, on the design system's modal layer (z-80). It used to render inline at
// z-50, and was reported hidden behind the sidebar's pop-up (Diogo, 2026-09-17).
//
// What is measured, on the real staff shell:
//   • `<aside class="platform-sidebar … translate-x-0">` computes to `position: static` with
//     `transform: matrix(1,0,0,1,0,0)`. The transform CREATES A STACKING CONTEXT; the DS's
//     `z-index: 70` on that rule is inert while it is static. On the PORTAL the same element is
//     `position: fixed`, where the 70 very much does apply.
//   • So the dialog's z-index was being resolved against sibling stacking contexts it had no
//     business competing with, and the answer differed between the two apps.
//
// Rather than tune a number against one layout, this removes the contest: at <body> level there
// is no ancestor context to be trapped in, and 80 is the DS's own `.platform-modal-overlay`
// layer — above `.platform-sidebar` (70) and `.platform-header` (60) wherever those are
// positioned. Deliberately BELOW `RowActionsMenu` (z-100, also body-level): that menu closes when
// it opens a dialog, so the two never coexist.

export function Dialog({ open, onClose, title, children, widthClassName }: DialogProps) {
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={cn(
          'flex max-h-[90vh] w-full flex-col rounded-lg border border-DEFAULT bg-surface shadow-card',
          widthClassName ?? 'max-w-lg',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* The X is the only affordance that SAYS the dialog can be closed. Escape and
            backdrop-click always worked but are invisible: Jorge, on the preview pop-up
            (2026-09-18) — "Como é que fechamos isto?" … "É fora. Mas [fechar] é cruz. E até
            podia ser a mesma terminologia." One X here, one wording, all 22 call sites. */}
        <div className="flex shrink-0 items-start gap-3 border-b border-DEFAULT px-6 py-4">
          <h2 className="min-w-0 flex-1 text-[14px] font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={pt.common.close}
            title={pt.common.close}
            className="-mr-1.5 -mt-0.5 shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
